import React, { useEffect, useRef, useState } from 'react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/Select';
import commandCenterService from '../../services/commandCenterService';

// Module-level cache so hospitals load once and never flicker on form re-renders
let cachedHospitals = null;
let hospitalsFetchPromise = null;

const CommandCenterHospitalField = ({
  value,
  onChange,
  onError,
  label = 'Partner Hospital',
  placeholder = 'Select partner hospital',
}) => {
  const [hospitals, setHospitals] = useState(() => cachedHospitals || []);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    let cancelled = false;

    if (cachedHospitals) {
      setHospitals(cachedHospitals);
      return;
    }

    if (!hospitalsFetchPromise) {
      hospitalsFetchPromise = commandCenterService.getHospitals({ limit: 100 });
    }

    hospitalsFetchPromise
      .then((result) => {
        const list = Array.isArray(result?.data) ? result.data : [];
        cachedHospitals = list;
        if (!cancelled) {
          setHospitals(list);
        }
      })
      .catch((error) => {
        hospitalsFetchPromise = null;
        if (!cancelled) {
          onErrorRef.current?.(error.message || 'Failed to load partner hospitals');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []); // Run strictly once on mount

  const currentHospitalName = value?.name || '';

  const handleSelectChange = (val) => {
    if (!val || val === '__none__') {
      onChange?.({ id: null, name: '' });
      return;
    }
    const matched = hospitals.find((h) => h.name === val || (h.hospital_id && h.hospital_id === val));
    onChange?.({
      id: matched ? (matched.hospital_id || matched.id) : null,
      name: matched ? matched.name : val,
    });
  };

  // Check if current value exists in hospitals list; if not but value exists (e.g. from existing booking), preserve it
  const isCustomOrLegacy =
    Boolean(currentHospitalName) &&
    !hospitals.some((h) => h.name.toLowerCase() === currentHospitalName.toLowerCase());

  return (
    <div>
      <label className="text-xs font-semibold text-gray-700 mb-1.5 block">{label}</label>
      <Select
        value={currentHospitalName || ''}
        onValueChange={handleSelectChange}
      >
        <SelectTrigger className="w-full h-9 sm:h-10 text-xs sm:text-sm">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-h-60">
          <SelectItem value="__none__">
            <span className="text-gray-400 italic">None (No Partner Hospital)</span>
          </SelectItem>
          {isCustomOrLegacy && (
            <SelectItem value={currentHospitalName}>
              <span className="font-medium text-gray-900">{currentHospitalName}</span>
            </SelectItem>
          )}
          {hospitals.map((h) => {
            const id = h.hospital_id || h.id || h.name;
            return (
              <SelectItem key={id} value={h.name}>
                <div className="flex items-center justify-between w-full gap-2">
                  <span className="truncate">{h.name}</span>
                  {h.city && (
                    <span className="text-gray-400 text-[10px] shrink-0 capitalize">
                      {h.city}
                    </span>
                  )}
                </div>
              </SelectItem>
            );
          })}
          {hospitals.length === 0 && !isCustomOrLegacy && (
            <div className="px-3 py-3 text-xs text-gray-400 text-center">
              No partner hospitals available
            </div>
          )}
        </SelectContent>
      </Select>
    </div>
  );
};

export default CommandCenterHospitalField;
